import { ApiError, request, send } from "./http.js";

export interface User {
  id: string;
  email: string;
  /**
   * The name others see beside what this account made (g68), or null for none.
   * Never replaced by the email. Absent from a user remembered by an older
   * build, until the next sign-in or name change brings it.
   */
  name?: string | null;
}

export interface EmailChange { id: string; email: string; expiresAt: number }

export async function requestEmailChange(email: string): Promise<EmailChange> {
  const normalized = email.trim().toLowerCase();
  const pending = await request<{ id: string; expiresAt: number }>("/api/account/email/request", {
    method: "POST", body: JSON.stringify({ email: normalized }), signal: AbortSignal.timeout(15_000),
  });
  return { ...pending, email: normalized };
}

export async function confirmEmailChange(pending: EmailChange, code: string): Promise<User> {
  const body = await request<{ user: User }>("/api/account/email/confirm", {
    method: "POST", body: JSON.stringify({ id: pending.id, email: pending.email, code: code.replace(/\s/g, "") }),
    signal: AbortSignal.timeout(15_000),
  });
  return body.user;
}

/**
 * Sets or removes the account's display name (g68). The server applies the
 * rules in `shared/display-name.ts` and answers with the name as stored, which
 * is remembered like the rest of the user so nothing refetches it at startup.
 * A refusal rejects with the server's `invalid_display_name` message.
 */
export async function updateDisplayName(name: string | null): Promise<User> {
  const body = await request<{ user: User }>("/api/account", {
    method: "PATCH", body: JSON.stringify({ displayName: name }), signal: AbortSignal.timeout(15_000),
  });
  rememberUser(body.user);
  return body.user;
}

/** One OAuth connection: a grant, as `/api/connections` describes it. */
export interface Connection {
  id: string;
  client: { id: string; name: string; host: string | null };
  createdAt: number;
  lastUsedAt: number | null;
}

/**
 * The account's connected apps. Account-level, like the email change above,
 * so it lives on the auth surface and never in the store: it is not list data,
 * it is never mirrored, and offline it simply fails.
 */
export async function listConnections(): Promise<Connection[]> {
  const body = await request<{ connections: Connection[] }>("/api/connections", {
    method: "GET", signal: AbortSignal.timeout(15_000),
  });
  return body.connections;
}

export async function disconnectConnection(id: string): Promise<void> {
  await request(`/api/connections/${encodeURIComponent(id)}`, {
    method: "DELETE", signal: AbortSignal.timeout(15_000),
  });
}

/**
 * The magic link lands on /auth/confirm?token=… Parsing is split out from
 * rendering so it can be tested without a DOM, and so a malformed link is a
 * value rather than an exception.
 */
export function confirmToken(url: string): string | undefined {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  if (parsed.pathname !== "/auth/confirm") return undefined;
  return parsed.searchParams.get("token") ?? undefined;
}

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && trimmed.length <= 254;
}

/**
 * `undefined` means signed out and nothing else.
 *
 * Anything other than a 401 is thrown, because the caller is the app's first
 * screen: a server that is unreachable must not be indistinguishable from a
 * session that expired. One of those is fixed by signing in again and the other
 * is not, and showing the sign-in form to someone who is merely on a train is
 * how a PWA loses the trust its offline story depends on.
 */
export async function currentUser(): Promise<User | undefined> {
  try {
    const body = await send<{ user: User }>("GET", "/api/me");
    return body.user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return undefined;
    throw error;
  }
}

/**
 * Resolves the same way whether or not the address has an account — the server
 * answers 202 either way, and the UI must not leak the difference. A transport
 * failure still rejects: that one is the caller's to show.
 */
/** `adding`: this sign-in finishes a guest's Add to my Lists (g132), so the server keeps the add it holds. */
export async function requestLink(email: string, adding = false): Promise<PendingSignIn> {
  await settlePendingLogout();
  const body = await send<{ codeExpiresAt: number }>("POST", "/api/auth/request", adding ? { email, adding: true } : { email });
  return { email, expiresAt: Math.min(body.codeExpiresAt, Date.now() + CODE_WINDOW_MS) };
}

export interface PendingSignIn { email: string; expiresAt: number }
const PENDING = "trayflux:pending-sign-in";
const CODE_WINDOW_MS = 10 * 60_000;

/** Presentation state only. The browser binding is an HttpOnly cookie. */
export function rememberPendingSignIn(pending: PendingSignIn): void {
  try { localStorage.setItem(PENDING, JSON.stringify({ email: pending.email, expiresAt: pending.expiresAt })); } catch { /* Current document still works. */ }
}

export function forgetPendingSignIn(expected?: PendingSignIn): void {
  try {
    if (expected) {
      const stored = JSON.parse(localStorage.getItem(PENDING) ?? "null") as PendingSignIn | null;
      if (stored?.email !== expected.email || stored?.expiresAt !== expected.expiresAt) return;
    }
    localStorage.removeItem(PENDING);
  } catch { /* Storage may be unavailable. */ }
}

export function pendingSignIn(): PendingSignIn | undefined {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(PENDING) ?? "null");
    if (value && typeof value === "object") {
      const { email, expiresAt } = value as Partial<PendingSignIn>;
      if (typeof email === "string" && isValidEmail(email) && typeof expiresAt === "number" &&
        Number.isSafeInteger(expiresAt) && expiresAt > Date.now() && expiresAt <= Date.now() + CODE_WINDOW_MS) {
        return { email, expiresAt };
      }
    }
  } catch { /* A malformed or inaccessible record grants no access. */ }
  forgetPendingSignIn();
  return undefined;
}

function forgetPendingFor(user: User): void {
  const pending = pendingSignIn();
  if (pending?.email.trim().toLowerCase() === user.email.toLowerCase()) forgetPendingSignIn(pending);
}

export interface Consumed {
  user: User;
  /**
   * Where to go instead of the app, when an OAuth authorize request was waiting
   * on this sign-in. Set by the server from a cookie it wrote itself, so it is
   * never a destination a caller supplied — and checked again here, because a
   * value that decides where a browser goes is worth checking twice.
   */
  next?: string;
}

/**
 * The only places a sign-in may send the browser on to: a pending OAuth
 * authorize request, the device activation page, or a shared list a guest
 * chose to add, now in their Lists (g132). The server already restricts
 * `next` to these; this is the second check.
 */
export function isSignInReturn(next: unknown): next is string {
  return typeof next === "string"
    && (next.startsWith("/api/oauth/authorize?") || next === "/activate" || /^\/shared\/[A-Za-z0-9_-]{22}$/.test(next));
}

export async function consume(token: string): Promise<Consumed | undefined> {
  // Before the try, which reads every refusal as a bad link.
  await settlePendingLogout();
  try {
    const body = await send<{ user: User; next?: string }>("POST", "/api/auth/consume", { token });
    const next = isSignInReturn(body.next) ? body.next : undefined;
    forgetPendingFor(body.user);
    return { user: body.user, ...(next ? { next } : {}) };
  } catch (error) {
    // A spent, expired or forged token is a 4xx, and the screen says the same
    // thing for all of them. A transport failure is not the link's fault.
    if (error instanceof ApiError && !error.offline) return undefined;
    throw error;
  }
}

export async function consumeCode(email: string, code: string): Promise<Consumed | undefined> {
  // Before the try, which reads every 4xx as a wrong code.
  await settlePendingLogout();
  try {
    const body = await send<Consumed>("POST", "/api/auth/consume-code", { email, code: code.replace(/\s/g, "") });
    const next = isSignInReturn(body.next) ? body.next : undefined;
    forgetPendingFor(body.user);
    return { user: body.user, ...(next ? { next } : {}) };
  } catch (error) {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) return undefined;
    throw error;
  }
}

/**
 * A Sign out the server has not confirmed (g65). The session cookie is
 * HttpOnly and outlives the wipe, so until the server ends that session a
 * reload would sign the account straight back in. The note says only that the
 * sign-out is unfinished: the cookie is one per browser, so it needs no name,
 * and Sign out promised to leave nothing of the account behind.
 *
 * The value is the time it was written. Its one job is compare-and-clear: a
 * late answer to one logout must not clear a note another tab wrote since.
 */
export const PENDING_LOGOUT = "trayflux:pending-logout";
export const PENDING_LOGOUT_FAILED = "Trayflux couldn't finish the last sign-out. Try again.";

/** Returns what was written, or `undefined` when storage refused it. Never throws. */
export function markPendingLogout(): string | undefined {
  const value = String(Date.now());
  try {
    localStorage.setItem(PENDING_LOGOUT, value);
    return value;
  } catch {
    // Sign out still sends the logout and still wipes; only the retry is lost.
    return undefined;
  }
}

export function pendingLogout(): string | undefined {
  try {
    return localStorage.getItem(PENDING_LOGOUT) ?? undefined;
  } catch {
    // Storage that cannot be read could not have been written either.
    return undefined;
  }
}

export function clearPendingLogout(expected: string | undefined): void {
  if (expected === undefined) return;
  try {
    if (localStorage.getItem(PENDING_LOGOUT) === expected) localStorage.removeItem(PENDING_LOGOUT);
  } catch { /* Nothing to clear. */ }
}

/**
 * Ends whatever session this browser's cookie holds. A 401 is success: that
 * session is already gone, or was never there. Offline or out of time is the
 * ordinary offline error; any other answer means the server has not said the
 * session ended, and says so in words a sign-in form can show.
 */
export async function logout(): Promise<void> {
  try {
    await request("/api/auth/logout", { method: "POST", signal: AbortSignal.timeout(15_000) });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return;
    if (error instanceof ApiError && error.offline) throw error;
    throw new ApiError(error instanceof ApiError ? error.status : 0, "pending_logout", PENDING_LOGOUT_FAILED);
  }
}

/**
 * Sends a pending logout, if there is one, and clears the note once the server
 * agrees. Every sign-in call runs it first: redeeming a link or code issues a
 * new session without ending the old one, so the note cannot simply be dropped
 * at sign-in, and a logout sent after the new cookie lands would end the new
 * session instead of the old.
 */
export async function settlePendingLogout(): Promise<void> {
  const pending = pendingLogout();
  if (pending === undefined) return;
  await logout();
  clearPendingLogout(pending);
}

export async function deleteAccount(email: string, expectedUserId: string): Promise<void> {
  await request("/api/account", { method: "DELETE", body: JSON.stringify({ email, expectedUserId }),
    signal: AbortSignal.timeout(15_000) });
}

export async function signOutForDeletion(): Promise<void> {
  try {
    await request("/api/auth/logout", { method: "POST", signal: AbortSignal.timeout(15_000) });
  } catch (error) {
    // An expired or already-deleted session cannot restore server data at startup.
    if (!(error instanceof ApiError && error.status === 401)) throw error;
  }
}

/**
 * Who this device was last signed in as — a user id and an address, kept where
 * a cold start can read it without asking anyone.
 *
 * Not a credential, and not a substitute for one: the session cookie is still
 * the only thing that opens a request, and it is HttpOnly and out of reach
 * here. This exists because `/api/me` cannot be reached from a train, and an
 * offline-first app that cannot name its user cannot open its mirror either —
 * which would make every reload away from the network a "Can't reach Trayflux"
 * page over a device with all the data already on it.
 *
 * Signing out clears it, along with the mirror and the queue it names.
 */
const REMEMBERED = "trayflux:user";

export function rememberUser(user: User): void {
  try {
    localStorage.setItem(REMEMBERED, JSON.stringify(user));
  } catch {
    // Private browsing, or site data switched off. The app still works with a
    // session it can reach; this only costs the offline start.
  }
}

export function rememberedUser(): User | undefined {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(REMEMBERED);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return undefined;
    const { id, email, name } = parsed as { id?: unknown; email?: unknown; name?: unknown };
    if (typeof id !== "string" || typeof email !== "string" || !id) return undefined;
    // An older build stored no name; that user is still this user.
    return typeof name === "string" || name === null ? { id, email, name } : { id, email };
  } catch {
    // Something else wrote here, or a half-written value survived a crash.
    return undefined;
  }
}

export function forgetUser(): void {
  try {
    localStorage.removeItem(REMEMBERED);
  } catch {
    // Nothing was stored in the first place.
  }
}
